import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';
import { OrderLineActionCode, OrderLineBrandStatus, ReturnStatus } from '@admin-panel/types';

class OrderLineItemAPIClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/order/lineitem');
  }

  async cancel(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/' + meta?.itemId + '/cancel';
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async acceptMTOPO(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      let url = this.baseURL + '/' + meta?.itemCode + '/po-accept';
      const response = await axiosInstance.put(`${url}`, {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async rejectMTOPO(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      let url = this.baseURL + '/' + meta?.itemCode + '/po-reject';
      const response = await axiosInstance.put(`${url}`, {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async performItemAction(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      let url = this.baseURL + '/' + meta?.itemId;

      const actionCode: OrderLineActionCode = meta?.actionCode;
      switch (actionCode) {
        case 'exchange':
          url += '/exchange';
          break;
        case 'alteration_complete':
          url += '/markAlterationComplete';
          break;
        case 'qc_fail_reship':
          url += '/reship';
          break;
        case 'customization_review_done':
          url += '/brandStatus/' + OrderLineBrandStatus.INTERNAL_REVIEW_DONE;
          break;
        case 'brand_accepted':
          url += '/brand-accept';
          break;
        case 'brand_declined':
          url += '/brand-decline';
          break;
        case 'brand_status_updated':
          url += '/vendor-status-update';
          break;
        case 'cancel':
          url = this.baseURL + '/bulkCancel';
          break;
        case 'b2b2c_brand_cancel':
          url = this.baseURL + '/brand-decline';
          break;
        case 'return':
          url = this.baseURL + '/bulkReturn';
          break;
        case 'brand_dispatched':
          url += '/brandStatus/' + OrderLineBrandStatus.DISPATCHED;
          break;
        case 'b2b_po_received_at_warehouse':
          url += '/brandStatus/' + OrderLineBrandStatus.RECEIVED_AT_WAREHOUSE;
          break;
      }
      // console.log('Calling api', url);

      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async updateReturnApproval(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: { action: string; returnRequestReason?: string },
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (payload.returnRequestReason) {
        queryParams.set('returnRequestReason', payload.returnRequestReason);
      }

      const queryString = queryParams.toString();
      const url =
        this.baseURL +
        '/' +
        meta?.itemId +
        '/return-approval/' +
        payload.action +
        (queryString ? `?${queryString}` : '');
      const response = await axiosInstance.put(`${url}`, {});
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async performExchangeAction(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      let url = this.baseURL;

      const targetStatus: ReturnStatus = payload.status;
      switch (targetStatus) {
        case ReturnStatus.CANCELLED:
          url += '/exchange/' + meta?.returnId + '/decline';
          break;
      }
      // console.log('Calling api', url);

      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async getByCode(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = this.baseURL + '/code/' + meta?.itemCode;
      const response = await axiosInstance.get(`${url}`);
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  }

  async recreatePoWithBrand(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta.id}/recreate-po-with-brand`,
      payload,
    );
    return response.data;
  }

  async reopenBrandDeclineForReview(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta.id}/reopen-brand-decline`,
      payload,
    );
    return response.data;
  }

  async addCustomCharge(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = `${this.baseURL}/${meta?.itemId}/custom-charges`;
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async updateVendorDeliveryDate(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      const url = `${this.baseURL}/${meta?.itemId}/vendor-delivery-date`;
      const response = await axiosInstance.put(`${url}`, payload);
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }
}

export default OrderLineItemAPIClient;
